// Night glows — warm additive halos that fade IN at night and OUT by day. They sit ON REAL LIGHT SOURCES ONLY: the lantern /
// brazier props of the buildings (synty.js adds one as a child of each such prop, so it follows the building — rotation, pop-in,
// removal) and the campfires. (The windows glow by themselves: the art's emission map lights them at night. There are no
// free-floating glows any more.) They're additive + depth-write-off so they bloom over the scene, with a gentle candle flicker.
// Plus a FEW small fireflies that buzz around near the player after dark.
//
// Driven by dayNight.js: setNight(f) every frame with f = 0 (day) … 1 (night).

import * as THREE from 'three'
import { GROUND_Y } from './hexGrid.js'

let group = null
let tex = null
let planeGeo = null
const glows = [] // { node, base, phase, flick } — node.userData.off = true switches one off (a burnt building's lanterns)
const nightObjects = [] // { obj, threshold } — Object3Ds shown only after dark (campfire flames)
const fireflies = [] // a few small fireflies: { sprite, x,y,z, vx,vy,vz, tvx,tvy,tvz, turn, ax,az, phase, period, on, base, size }
let flyTex = null
let flyGroup = null
let flyFocus = { x: 0, z: 0, on: true } // where the player is: the fireflies stay around it
let lastFlyT = 0
let pruneAt = 0 // next time (ms) to drop glows whose object has left the scene

function makeTexture() {
  const cv = document.createElement('canvas')
  cv.width = cv.height = 128
  const ctx = cv.getContext('2d')
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64)
  g.addColorStop(0.00, 'rgba(255,248,224,1)')
  g.addColorStop(0.16, 'rgba(255,228,172,0.9)')
  g.addColorStop(0.42, 'rgba(255,182,102,0.38)')
  g.addColorStop(1.00, 'rgba(255,160,80,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 128, 128)
  const t = new THREE.CanvasTexture(cv)
  t.colorSpace = THREE.SRGBColorSpace
  return t
}

function ensureAssets() {
  if (!tex) tex = makeTexture()
  if (!planeGeo) planeGeo = new THREE.PlaneGeometry(1, 1)
}

export function initNightLights(scene) {
  if (group) return
  group = new THREE.Group()
  scene.add(group)
  ensureAssets()
}

// A glow that belongs to an object (a lantern prop): a child of `parent`, positioned in the parent's local space, so it moves,
// turns, scales and disappears with it. `size` is in the PARENT's units (the parent may be scaled down). Returns the node.
export function addGlowTo(parent, x, y, z, { size = 1, intensity = 0.7, color = '#ffb25a', flicker = 0.12 } = {}) {
  ensureAssets()
  const mat = new THREE.SpriteMaterial({
    map: tex, color: new THREE.Color(color),
    transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0,
  })
  const node = new THREE.Sprite(mat)
  node.scale.set(size, size, 1)
  node.position.set(x, y, z)
  node.visible = false
  parent.add(node)
  glows.push({ node, base: intensity, phase: Math.random() * 6.28, flick: flicker })
  return node
}

// Add a warm glow at a world position. size = diameter (world units); intensity
// = peak opacity at full night; flicker = candle wobble (0–1). flat = true lays
// it FLAT on the ground (a light pool) instead of a camera-facing billboard.
export function addGlow(x, y, z, { size = 1.2, intensity = 0.9, color = '#ffd28a', flicker = 0.12, flat = false } = {}) {
  if (!group) return
  let node
  if (flat) {
    const mat = new THREE.MeshBasicMaterial({
      map: tex, color: new THREE.Color(color),
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0,
    })
    node = new THREE.Mesh(planeGeo, mat)
    node.rotation.x = -Math.PI / 2 // lie flat on the ground
    node.scale.set(size, size, 1)
  } else {
    const mat = new THREE.SpriteMaterial({
      map: tex, color: new THREE.Color(color),
      transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0,
    })
    node = new THREE.Sprite(mat)
    node.scale.set(size, size, 1)
  }
  node.position.set(x, y, z)
  node.visible = false
  group.add(node)
  glows.push({ node, base: intensity, phase: Math.random() * 6.28, flick: flicker })
}

// Register an Object3D that should only be VISIBLE after dark (e.g. a campfire
// flame) — toggled by setNight() against the night factor.
export function addNightObject(obj, threshold = 0.12) {
  if (obj) nightObjects.push({ obj, threshold })
}

function makeFireflyTexture() {
  const cv = document.createElement('canvas')
  cv.width = cv.height = 32
  const ctx = cv.getContext('2d')
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16)
  g.addColorStop(0, 'rgba(255,255,205,1)')
  g.addColorStop(0.16, 'rgba(235,255,150,0.95)')
  g.addColorStop(0.42, 'rgba(190,245,110,0.42)')
  g.addColorStop(1, 'rgba(150,225,90,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, 32, 32)
  const t = new THREE.CanvasTexture(cv)
  t.colorSpace = THREE.SRGBColorSpace
  return t
}

// ── Fireflies ───────────────────────────────────────────────────────────────────────────────────────────────────────────
// A FEW small fireflies (default 7 — a handful on screen at once) that buzz about near the player after dark: they dart in short
// bursts (a new random heading every 0.15–0.65 s, fast steering, a tiny jitter), stay loosely tethered to a spot, and blink the
// way real ones do — a short glow, then dark (with a faint ember between flashes). They fade in as the evening comes on (about
// 19:30, full by 22:00) and out by the small hours. They follow the player: one that drifts too far is re-seated near them while dark.
const FLY_NEAR = 3.6   // world units: fireflies live within about this of the player
const FLY_FAR = 6.5    // beyond this a firefly is moved back in (once it is dark, or at once if very far)

function seatFirefly(f) {
  const a = Math.random() * Math.PI * 2, r = 0.8 + Math.sqrt(Math.random()) * (FLY_NEAR - 0.8)
  f.ax = flyFocus.x + Math.cos(a) * r
  f.az = flyFocus.z + Math.sin(a) * r
  f.x = f.ax; f.z = f.az
  f.y = GROUND_Y + 0.25 + Math.random() * 0.6
}

export function initFireflies(count = 7) {
  if (flyGroup || !group) return
  ensureAssets()
  flyTex = makeFireflyTexture()
  flyGroup = new THREE.Group()
  group.parent.add(flyGroup)
  for (let i = 0; i < count; i++) {
    const mat = new THREE.SpriteMaterial({ map: flyTex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, opacity: 0 })
    const sprite = new THREE.Sprite(mat)
    const size = 0.07 + Math.random() * 0.04 // small: about 12–20 px on screen, bright core ~5 px
    sprite.scale.set(size, size, 1)
    sprite.visible = false
    flyGroup.add(sprite)
    const f = { sprite, size, phase: Math.random() * 6.28, period: 1.7 + Math.random() * 1.6, on: 0.3 + Math.random() * 0.15, base: 0.8 + Math.random() * 0.2, turn: 0, vx: 0, vy: 0, vz: 0, tvx: 0, tvy: 0, tvz: 0, x: 0, y: 0, z: 0, ax: 0, az: 0 }
    seatFirefly(f)
    fireflies.push(f)
  }
}

// Where the player is (the fireflies stay around them). on = false hides them (the player is away in the Capital / an adventure).
export function setFireflyFocus(x, z, on = true) { flyFocus = { x, z, on } }

function tickFireflies(f01) {
  if (!fireflies.length) return
  const now = performance.now() * 0.001
  const dt = Math.min(0.1, Math.max(0, now - lastFlyT)); lastFlyT = now
  const dim = flyFocus.on ? Math.min(1, Math.max(0, (f01 - 0.55) / 0.4)) : 0 // the evening ramp: nothing by day, full from about 22:00
  if (flyGroup) flyGroup.visible = dim > 0.01
  if (dim <= 0.01) return
  for (const fly of fireflies) {
    const s = fly.sprite
    s.visible = true
    // blink: a short smooth flash each period, with a faint ember in between
    const u = ((((now / fly.period) + fly.phase) % 1) + 1) % 1
    const env = u < fly.on ? Math.pow(Math.sin(Math.PI * u / fly.on), 2) : 0
    const glow = 0.1 + 0.9 * env
    // too far from the player: re-seat near them (while dark, so it doesn't visibly jump; at once if very far)
    const far = Math.hypot(fly.x - flyFocus.x, fly.z - flyFocus.z)
    if (far > FLY_FAR && (env < 0.02 || far > FLY_FAR * 2)) seatFirefly(fly)
    // buzzing: a new random heading every so often, steered into fast, with a tiny jitter on top
    fly.turn -= dt
    if (fly.turn <= 0) {
      const ang = Math.random() * Math.PI * 2, sp = 0.35 + Math.random() * 0.75
      fly.tvx = Math.cos(ang) * sp; fly.tvz = Math.sin(ang) * sp; fly.tvy = (Math.random() - 0.5) * 0.5
      fly.turn = 0.15 + Math.random() * 0.5
    }
    const k = 1 - Math.exp(-9 * dt)
    fly.vx += (fly.tvx + (fly.ax - fly.x) * 0.5 - fly.vx) * k // (a gentle pull toward its spot keeps it from wandering off)
    fly.vz += (fly.tvz + (fly.az - fly.z) * 0.5 - fly.vz) * k
    fly.vy += (fly.tvy - fly.vy) * k
    fly.x += fly.vx * dt; fly.z += fly.vz * dt; fly.y += fly.vy * dt
    fly.y = Math.min(GROUND_Y + 1.0, Math.max(GROUND_Y + 0.15, fly.y))
    s.position.set(fly.x + Math.sin(now * 41 + fly.phase) * 0.006, fly.y + Math.sin(now * 53 + fly.phase * 2) * 0.006, fly.z + Math.cos(now * 47 + fly.phase) * 0.006)
    s.material.opacity = fly.base * glow * dim
  }
}

export function clearGlows() { // (removes every registered glow — the fireflies are not part of this)
  for (const g of glows) { g.node.parent?.remove(g.node); g.node.material?.dispose?.() }
  glows.length = 0
}

// Drop glows whose object has left the scene (a building that was removed, a visited kingdom that was closed).
function pruneGlows() {
  for (let i = glows.length - 1; i >= 0; i--) {
    let o = glows[i].node
    while (o.parent) o = o.parent
    if (!o.isScene) { glows[i].node.material?.dispose?.(); glows.splice(i, 1) }
  }
}

// f: 0 (day → all off) … 1 (night → full). Soft per-glow flicker adds life.
export function setNight(f) {
  for (const o of nightObjects) o.obj.visible = f > o.threshold // campfire flames etc.
  tickFireflies(f)
  if (!glows.length) return
  const nowMs = performance.now()
  if (nowMs >= pruneAt) { pruneAt = nowMs + 2000; pruneGlows() } // (every couple of seconds, by the clock — not by frames, which a slow page draws rarely)
  if (f <= 0.002) {
    for (const g of glows) g.node.visible = false
    return
  }
  const t = performance.now() * 0.004
  for (const g of glows) {
    g.node.visible = !g.node.userData.off
    const fl = 1 - g.flick + g.flick * Math.sin(t + g.phase) // (1-2·flick)…1
    g.node.material.opacity = g.base * f * fl
  }
}

// Dev / test hook: where the lantern glows and fireflies are (world units) and what state they are in.
if (import.meta.env?.DEV && typeof window !== 'undefined') {
  const pos = (o) => { const v = new THREE.Vector3(), sc = new THREE.Vector3(); o.getWorldPosition(v); o.getWorldScale(sc); return { x: v.x, y: v.y, z: v.z, size: sc.x, vis: o.visible, op: o.material.opacity } }
  window.__night = {
    glows: () => glows.map((g) => {
      let w = g.node.parent; while (w && !w.userData?.synty) w = w.parent // the building (recipe group) this glow belongs to, if any
      const wp = w ? w.getWorldPosition(new THREE.Vector3()) : null
      return { ...pos(g.node), off: !!g.node.userData.off, building: w?.userData?.synty ?? null, bx: wp?.x, bz: wp?.z }
    }),
    prune: () => pruneGlows(),
    fireflies: () => fireflies.map((f) => pos(f.sprite)),
    visibleFireflies: () => (flyGroup?.visible ? fireflies.filter((f) => f.sprite.visible && f.sprite.material.opacity > 0.05).length : 0),
  }
}
